import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Minus, Plus } from "lucide-react";
import { AFG_CONTRACT, AFG_SCAN, NX_CONTRACT, NX_SCAN } from "../../data/site";
import { useLanguage } from "../../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";

const QA = [
    { q: "What is Nexora Global?", a: "Nexora Global is a global Web3 / DeFi ecosystem built around smart-contract technology, blockchain infrastructure, structured distribution and a community-driven ecosystem." },
    { q: "What network does Nexora use?", a: "The Nexora ecosystem is built around BNB Smart Chain (BEP20), with USDT BEP20 used for joining and distribution." },
    { q: "What is the minimum joining amount?", a: "The minimum joining amount is 5 USDT (BEP20), plus a 0.50 USDT system fee and a $0.50 BNB network fee." },
    { q: "What is the Nexora smart contract?", a: "The Nexora Global smart contract is public on BNB Smart Chain and can be explored on BscScan:" },
    { q: "What is the AFG token contract?", a: "The AFG token contract for the ecosystem is public on BscScan:" },
    { q: "What is the AFG total supply?", a: "The AFG token has a total supply of 500,000 AFG and a max supply of 500,000 AFG." },
    { q: "Which wallets are supported?", a: "Trust Wallet, TokenPocket, MetaMask, Binance Wallet and SafePal — plus more compatible BEP20 wallets." },
    { q: "How does the 7-level affiliate distribution work?", a: "Affiliate distribution runs across seven levels: L1 40%, L2 15%, L3 15%, L4 10%, L5 10%, L6 5% and L7 5% — totaling 100%." },
    { q: "How does the matrix system work?", a: "Each level has a 12-slot matrix with 10 active slots and 2 recycle slots (11 and 12), split across first-line, second-line and recycle positions as described in the project material." },
    { q: "What is the AFG referral bonus?", a: "You receive 2 AFG for every direct referral — until a total of 100,000 AFG has been distributed through this mechanism." },
    { q: "What is the AFG registration bonus?", a: "You receive 2 AFG for registering — available to the first 50,000 registrations only." },
    { q: "What is the Next Global Matrix?", a: "A 12-level matrix with prices from $5 to $2,000. Each level price is distributed as 10% Salary Fund, 50% Affiliate and 40% Matrix." },
    { q: "What is the Monthly Salary Fund?", a: "The Monthly Salary Fund is distributed as 35% to the Top 50 Leaders, 20% to active Levels 2–6 (with the stated team conditions) and 45% to active Levels 7–12." },
    { q: "What products are planned for the future?", a: "Nexora Global, Token Trade, Gold Global Program, AFG Token, AFG DEX Listing and Nexora Wallet are all listed as Coming Soon." },
    { q: "Where can I find the official Nexora community?", a: "Through the official Telegram Group, Telegram Channel, TikTok, YouTube and the official website — all linked in the community section above." },
];

const AddressBlock = ({ address, scanUrl, label, testId }) => {
    const { t } = useLanguage();
    return (
        <div className="mt-3 rounded-xl border border-white/10 bg-nx-soft p-4">
            <p className="break-all font-mono text-xs leading-relaxed text-nx-text" dir="ltr" data-testid={`${testId}-address`}>
                {address}
            </p>
            <a
                href={scanUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${testId}-scan`}
                className="group mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-nx-blue transition-colors duration-300 hover:text-nx-lime"
            >
                {t(label)}
                <ArrowUpRight size={12} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
            </a>
        </div>
    );
};

export const Faq = () => {
    const { t } = useLanguage();
    const [open, setOpen] = useState(0);

    return (
        <section id="faq" data-testid="faq-section" className="border-t border-white/5 bg-nx-alt py-20 sm:py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                        <SectionHeader
                            index="07"
                            label={t("FAQ")}
                            testId="faq"
                            title={<>{t("Frequently Asked")}<br />{t("Questions")}</>}
                            description={t("Everything you need to understand the Nexora Global ecosystem.")}
                        />
                    </div>
                    <div className="lg:col-span-8">
                        <Reveal delay={0.1}>
                            <div className="overflow-hidden rounded-2xl border border-white/5 bg-nx-elev" data-testid="faq-accordion">
                                {QA.map((item, i) => (
                                    <div key={item.q} className="border-b border-white/5 last:border-0">
                                        <button
                                            type="button"
                                            onClick={() => setOpen(open === i ? -1 : i)}
                                            data-testid={`faq-q-${i + 1}`}
                                            aria-expanded={open === i}
                                            className="flex w-full items-center justify-between gap-4 px-6 py-5 text-start transition-colors duration-200 hover:bg-white/[0.02]"
                                        >
                                            <span className={`text-sm font-bold transition-colors duration-300 ${open === i ? "text-nx-text" : "text-nx-sub"}`}>
                                                {t(item.q)}
                                            </span>
                                            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${open === i ? "border-nx-lime/40 text-nx-lime" : "border-white/10 text-nx-sub"}`}>
                                                {open === i ? <Minus size={13} strokeWidth={2} aria-hidden="true" /> : <Plus size={13} strokeWidth={2} aria-hidden="true" />}
                                            </span>
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {open === i && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-6 pb-6 text-sm leading-relaxed text-nx-sub" data-testid={`faq-a-${i + 1}`}>
                                                        <p>{t(item.a)}</p>
                                                        {i === 3 && <AddressBlock address={NX_CONTRACT} scanUrl={NX_SCAN} label="View on BscScan" testId="faq-nx-contract" />}
                                                        {i === 4 && <AddressBlock address={AFG_CONTRACT} scanUrl={AFG_SCAN} label="View AFG on BscScan" testId="faq-afg-contract" />}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
